import React, { useCallback, useState } from 'react';
import {
  MemberCampaignItem,
  MemberMgSupplierCampaignItem,
  MemberMgSupplierCampaignItemLookup,
  MemberMgSupplierCampaignItemLookupDTO,
  MgSupplierItemLookupDTO,
} from '@retail/campaign/types';
import {
  addCampaignItemsSchema,
  AddCampaignItemsSchema,
  memberCampaignItemSchema,
  MemberCampaignItemSchema,
  useGetAddMemberCampaignItemsColumns,
} from '@retail/campaign/components';
import { useAppTFunction } from '@retail/app/i18n';
import { EditCampaignItemsDialog } from './EditCampaignItemsDialog';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLanguageSelector } from '@retail/app/hooks';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useLookupMemberCampaignItems } from '@retail/campaign/data-access';
import { useSearchParams } from 'react-router';

interface Props {
  campaignId: string;
  campaignItems: MemberCampaignItem[];
  onClose: () => void;
}

function MemberEditCampaignItemsDialogComponent({ campaignId, campaignItems, onClose }: Props) {
  const { assortmentId, appTenant } = useSelectedOrgUnit();
  const getCampaignItemColumns = useGetAddMemberCampaignItemsColumns({
    externalSourceType: appTenant.externalItemSource,
  });
  const { language } = useLanguageSelector();

  const [, setURLSearchParams] = useSearchParams();
  const [lookupItemErrors, setLookupItemErrors] = useState<MemberMgSupplierCampaignItemLookupDTO[]>(
    []
  );
  const [lookupItems, setLookupItems] = useState<MemberMgSupplierCampaignItem[]>([]);

  const { mutateAsync: handleLookupItems, isPending: isPendingLookupItems } =
    useLookupMemberCampaignItems<MemberMgSupplierCampaignItemLookupDTO>({
      assortmentId,
    });

  const performItemsLookup = useCallback(
    (lookupSearchItems: MgSupplierItemLookupDTO[]) => {
      handleLookupItems({
        body: { assortmentId, itemLookups: lookupSearchItems },
      }).then((response) => {
        const itemsWithErrors = response.filter((item) => item.errorCode);
        if (itemsWithErrors.length) {
          setLookupItemErrors(itemsWithErrors);
          return;
        }

        const items = response
          .map((item) => MemberMgSupplierCampaignItemLookup.fromDto(item, language))
          .flatMap((lookup) => lookup.items ?? []);
        setLookupItems(items);
        setURLSearchParams();
      });
    },
    [handleLookupItems, language, assortmentId, setURLSearchParams]
  );

  return (
    <EditCampaignItemsDialog
      open
      campaignId={campaignId}
      campaignItems={campaignItems}
      onClose={onClose}
      type="member"
      getCampaignItemsColumns={getCampaignItemColumns}
      transformToPutDTO={(item, _, campaignItem) => ({
        id: campaignItem.id,
        campaignUnit: item.campaignUnit,
        campaignSalesPrice: item.campaignSalesPrice ?? null,
        campaignPurchasePrice: item.campaignPurchasePrice ?? undefined,
      })}
      isPendingLookupItems={isPendingLookupItems}
      performItemsLookup={performItemsLookup}
      lookupItemErrors={lookupItemErrors}
      lookupItems={lookupItems}
      setLookupItems={setLookupItems}
    />
  );
}

export function MemberEditCampaignItemsDialog(props: Props) {
  const t = useAppTFunction();
  const formItems: AddCampaignItemsSchema<MemberCampaignItemSchema>['items'] =
    props.campaignItems.reduce(
      (prev, item) => ({
        ...prev,
        [item.mgSupplierItemId + '']:
          // @ts-ignore
          {
            campaignUnit: item.campaignUnit,
            campaignSalesPrice: item.getSalesPriceForCampaignUnit()?.priceWithVat || '',
            campaignPurchasePrice: item.getPurchasePriceForCampaignUnit()?.priceWithoutVat || '',
          } as MemberCampaignItemSchema,
      }),
      {}
    );

  const formMethods = useForm<AddCampaignItemsSchema<MemberCampaignItemSchema>>({
    resolver: yupResolver(addCampaignItemsSchema(memberCampaignItemSchema(t))),
    shouldUnregister: false,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      items: formItems,
    },
  });

  return (
    <FormProvider {...formMethods}>
      <MemberEditCampaignItemsDialogComponent {...props} />
    </FormProvider>
  );
}
